import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: \"Snowflake MFA Setup: Enabling & Disabling\"\ndescription: \"In this post, Ian explains how to enable MFA in your Snowflake account, and how account admins can disable it for a given user.\"\nauthorNames: [ian]\n---\n\nMulti-factor authentication (MFA) can be used for free by all Snowflake customers to provide increased security when connecting to Snowflake. Let's talk about how you can get setup with MFA in Snowflake.\n\n## How to enable MFA for a Snowflake User?\n\nTo enable MFA for your Snowflake user, click on your profile in the Snowsight UI:\n\n{% blog-post-image imageName=\"snowflake-mfa-0-eb999c632e20d9f9a894836b7c1b15315ba5b6237b16ec8b8c992f83d850a759\" alt=\"\"/%}\n\nAt the bottom of your profile card, you'll see a section for **Multi-factor authentication**. Click the **Enroll** button:\n\n{% blog-post-image imageName=\"snowflake-mfa-1-086d99204c290ae4c454d67a42b259939f56e43508217259d3b1f763c2583a2e\" alt=\"\"/%}\n\nSnowflake's MFA feature is powered by [Cisco Duo](https://duo.com/). You do not need to separately sign up for Duo, but you will need to download the [Duo Mobile Application](https://guide.duo.com/) on your phone.\n\nOnce enrolled, you'll see a [screen like this](https://docs.snowflake.com/en/user-guide/security-mfa#using-mfa-with-sf-web-interface) the next time you log in.\n\n## How to disable MFA in Snowflake?\n\nDisabling MFA can only be done programmatically with SQL by a user with the [ACCOUNTADMIN role](/posts/snowflake-roles#default-snowflake-account-roles). Here's the command to disable MFA for a user:\n\n```sql\nuse role accountadmin;\nalter user ian set disable_mfa=true;\n```\n\n## Is MFA enabled by default?\n\nMFA is not enabled by default in Snowflake. It must be enabled on a per-user basis and users are not automatically enrolled in MFA. Administrators cannot automatically enforce that all users require MFA. They can instead build automations using the queries below to identify users without MFA and take the appropriate action.\n\n## Identify all users without MFA enabled\n\nTo identify users without MFA enabled, you can query the `snowflake.account_usage.users` view (details [here](https://docs.snowflake.com/en/sql-reference/account-usage/users)). \n\n```sql\nselect *\nfrom snowflake.account_usage.users\nwhere\n    ext_authn_duo = false\n    and deleted_on is null\norder by last_success_login desc\n;\n```\n\nNote, this view can be up to 2 hours stale. For an up-to-date copy of the data, run the command `show users`.\n\nThe output of this query will likely contain many machine users, for which it doesn't make sense to enable MFA. Instead, it is recommended to setup [key-pair authentication](/docs/snowflake-developer-guide/snowflake-key-pair) for these users, instead of the traditional username-password authentication method.\n\n## Identify all PRIVILEGED users without MFA\n\nIt's especially important to identify any users without MFA who have privileged access (one of the [default `SYSADMIN`, `SECURITYADMIN`, or `ACCOUNTADMIN` roles](https://select.dev/posts/snowflake-roles#default-snowflake-account-roles)), as a compromise of one of these users would be much more significant.\n\nThe following query identifies all users with access to one of these roles who do not have MFA enabled:\n\n```sql\nwith \nprivileged_roles (role_name) as (\n    select grantee_name\n    from snowflake.account_usage.grants_to_roles\n    where\n        privilege = 'MANAGE GRANTS'\n        or grantee_name in ('SYSADMIN', 'SECURITYADMIN', 'ACCOUNTADMIN')\n)\nselect\n    users.name,\n    users.has_password,\n    timediff(days, users.last_success_login, current_timestamp())|| ' days ago' as last_login,\n    timediff(days, users.password_last_set_time, current_timestamp(6)) || ' days ago' as password_age\nfrom snowflake.account_usage.grants_to_users\ninner join privileged_roles\n    on grants_to_users.role = privileged_roles.role_name\n    and grants_to_users.deleted_on is null\ninner join snowflake.account_usage.users\n    on grants_to_users.grantee_name = users.name\n    and users.ext_authn_duo = false\n    and users.deleted_on is null\norder by users.last_success_login desc\n;\n```\n\n## What other security measures should you implement?\n\nIn addition to enabling MFA for all human users in your Snowflake Account, consider the following additional security measures recommended by Snowflake:\n\n* Use [key pair authentication](/docs/snowflake-developer-guide/snowflake-key-pair) for all users without MFA\n* Rotate passwords frequently\n* Utilize account-level Network Policies to only allow logins from known IP addresses for all users\n* Utilize user-level Network to ensure service accounts can be only used from a more specific list of IP's\n* Use Authentication Policies to ensure service accounts cannot use the UI\n* Create a custom role for each service account and ensure least-privilege\n* Consider using Session Policies and Password Policies\n* Utilize a username naming convention to monitor all SQL closely and have a low threshold for suspicious activities";
const filepath = "/docs/snowflake-developer-guide/snowflake-mfa.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
